import React, { useState, useEffect, useCallback } from 'react';
import onboardingConfig from '../../onboarding.config.json';

const OnboardingHelpButton = ({ windowName = "main" }) => {
  const [windowHintsEnabled, setWindowHintsEnabled] = useState({});

  // Initialize state from localStorage
  useEffect(() => {
    const storedVersion = localStorage.getItem('onboarding.version');
    const storedWindowStates = JSON.parse(localStorage.getItem('onboarding.windowStates') || '{}');

    // Version check - reset if version changed
    if (storedVersion !== onboardingConfig.version) {
      localStorage.setItem('onboarding.version', onboardingConfig.version);
      const initialStates = { [windowName]: false };
      localStorage.setItem('onboarding.windowStates', JSON.stringify(initialStates));
      localStorage.setItem('onboarding.hidden', '[]');
      setWindowHintsEnabled(initialStates);
    } else {
      // Initialize current window to false if not set (hints disabled by default)
      if (storedWindowStates[windowName] === undefined) {
        storedWindowStates[windowName] = false;
        localStorage.setItem('onboarding.windowStates', JSON.stringify(storedWindowStates));
      }
      setWindowHintsEnabled(storedWindowStates);
    }
  }, [windowName]);

  // Listen for onboarding toggle events (e.g., from Skip button)
  useEffect(() => {
    const handleToggleEvent = (event) => {
      if (event.detail.windowName === windowName) {
        setWindowHintsEnabled(prev => ({
          ...prev,
          [windowName]: event.detail.enabled
        }));
      }
    };

    window.addEventListener('onboardingToggle', handleToggleEvent);
    return () => window.removeEventListener('onboardingToggle', handleToggleEvent);
  }, [windowName]);

  // Function to toggle hints for current window
  const toggleWindowHints = useCallback(() => {
    setWindowHintsEnabled(prev => {
      const newState = !prev[windowName];
      const newWindowStates = { ...prev, [windowName]: newState };

      console.log(`🔄 Toggling hints for ${windowName}: ${!newState} → ${newState}`);

      localStorage.setItem('onboarding.windowStates', JSON.stringify(newWindowStates));

      // Dispatch event after state update
      setTimeout(() => {
        if (newState) {
          // When enabling, reset dismissed hints
          console.log(`✅ Enabling hints for ${windowName}, clearing dismissed hints`);
          localStorage.setItem('onboarding.hidden', '[]');
        }

        // Trigger a custom event to notify OnboardingHints components
        window.dispatchEvent(new CustomEvent('onboardingToggle', {
          detail: { windowName, enabled: newState }
        }));
      }, 0);

      return newWindowStates;
    });
  }, [windowName]);

  const isWindowEnabled = windowHintsEnabled[windowName] === true;
  const windowConfig = onboardingConfig.windows[windowName];
  const windowDisplayName = windowConfig ? windowConfig.name : windowName;

  return (
    <button
      className="relative flex h-8 w-8 items-center justify-center rounded-full border border-solid border-brand-orange-600 text-brand-orange-600 hover:brightness-110 animate-pulse-subtle transition-all duration-200 md:h-10 md:w-10"
      onClick={toggleWindowHints}
      onDoubleClick={() => {
        // Double-click to force reset everything
        console.log('🔄 Force reset all onboarding data');
        localStorage.setItem('onboarding.version', 'v0');
        localStorage.setItem('onboarding.windowStates', '{}');
        localStorage.setItem('onboarding.hidden', '[]');
        window.location.reload();
      }}
      title={`${isWindowEnabled ? 'Hide' : 'Show'} hints for ${windowDisplayName}. Double-click to reset all.`}
      aria-label={`${isWindowEnabled ? 'Hide' : 'Show'} onboarding hints for ${windowDisplayName}`}
    >
      <svg width="7" height="15" viewBox="0 0 7 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M-0.000648417 2.165C0.203352 1.985 0.425352 1.814 0.665352 1.652C0.911352 1.49 1.17535 1.349 1.45735 1.229C1.73935 1.109 2.04535 1.016 2.37535 0.95C2.70535 0.878 3.06235 0.842 3.44635 0.842C3.96835 0.842 4.44235 0.914 4.86835 1.058C5.30035 1.202 5.66935 1.409 5.97535 1.679C6.28135 1.943 6.51835 2.264 6.68635 2.642C6.85435 3.02 6.93835 3.443 6.93835 3.911C6.93835 4.367 6.87235 4.763 6.74035 5.099C6.60835 5.429 6.44335 5.717 6.24535 5.963C6.04735 6.209 5.82835 6.422 5.58835 6.602C5.35435 6.782 5.13235 6.953 4.92235 7.115C4.71235 7.271 4.52935 7.427 4.37335 7.583C4.22335 7.733 4.13335 7.904 4.10335 8.096L3.89635 9.41H2.37535L2.22235 7.943C2.18635 7.661 2.22535 7.415 2.33935 7.205C2.45935 6.989 2.61535 6.794 2.80735 6.62C3.00535 6.44 3.22435 6.269 3.46435 6.107C3.70435 5.939 3.92935 5.759 4.13935 5.567C4.34935 5.369 4.52335 5.15 4.66135 4.91C4.80535 4.664 4.87735 4.373 4.87735 4.037C4.87735 3.821 4.83535 3.629 4.75135 3.461C4.67335 3.287 4.56235 3.14 4.41835 3.02C4.27435 2.894 4.10035 2.798 3.89635 2.732C3.69835 2.666 3.48235 2.633 3.24835 2.633C2.90635 2.633 2.61535 2.672 2.37535 2.75C2.14135 2.822 1.94035 2.906 1.77235 3.002C1.61035 3.092 1.47235 3.176 1.35835 3.254C1.24435 3.326 1.14235 3.362 1.05235 3.362C0.836352 3.362 0.680352 3.272 0.584352 3.092L-0.000648417 2.165ZM1.70035 12.776C1.70035 12.59 1.73335 12.413 1.79935 12.245C1.87135 12.077 1.96735 11.933 2.08735 11.813C2.20735 11.693 2.35135 11.597 2.51935 11.525C2.68735 11.453 2.86735 11.417 3.05935 11.417C3.25135 11.417 3.42835 11.453 3.59035 11.525C3.75835 11.597 3.90235 11.693 4.02235 11.813C4.14835 11.933 4.24735 12.077 4.31935 12.245C4.39135 12.413 4.42735 12.59 4.42735 12.776C4.42735 12.968 4.39135 13.148 4.31935 13.316C4.24735 13.478 4.14835 13.619 4.02235 13.739C3.90235 13.859 3.75835 13.952 3.59035 14.018C3.42835 14.09 3.25135 14.126 3.05935 14.126C2.86735 14.126 2.68735 14.09 2.51935 14.018C2.35135 13.952 2.20735 13.859 2.08735 13.739C1.96735 13.619 1.87135 13.478 1.79935 13.316C1.73335 13.148 1.70035 12.968 1.70035 12.776Z" fill="currentColor"></path>
      </svg>
    </button>
  );
};

export default OnboardingHelpButton;
